<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="1200px" scrollable>
    <v-card v-if="kiosk">
      <!-- Header -->
      <v-card-title class="headline primary white--text d-flex align-center">
        <v-icon dark class="mr-3">mdi-desktop-classic</v-icon>
        <div>
          <div>{{ kiosk.name }}</div>
          <div class="text-subtitle-2 font-weight-light">{{ kiosk.id }}</div>
        </div>
        <v-spacer />
        <v-chip v-if="kiosk.openedTicket" color="warning" dark class="mr-2">
          <v-icon left small>mdi-ticket</v-icon>
          Ticket ouvert
        </v-chip>
        <v-btn icon dark @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-4">
        <!-- Alerts Section -->
        <v-row v-if="activeAlerts.length > 0" class="mb-4">
          <v-col cols="12">
            <v-alert type="warning" outlined dense class="mb-0">
              <div class="d-flex align-center flex-wrap">
                <strong class="mr-3">{{ activeAlerts.length }} alerte(s) active(s) :</strong>
                <v-chip v-for="alert in activeAlerts" :key="alert.key" :color="alert.color" dark small class="ma-1">
                  <v-icon left x-small>{{ alert.icon }}</v-icon>
                  {{ alert.label }}
                </v-chip>
              </div>
            </v-alert>
          </v-col>
        </v-row>

        <!-- Main Stats Cards -->
        <v-row>
          <!-- Connexion Status -->
          <v-col cols="12" md="4">
            <v-card outlined class="fill-height">
              <v-card-title class="text-subtitle-1 pb-2">
                <v-icon left color="blue">mdi-wifi</v-icon>
                Connexion
              </v-card-title>
              <v-card-text>
                <div class="stat-item">
                  <span class="stat-label">Dernier ping Nuage</span>
                  <v-chip :color="getDateColor(kiosk.lastPing, 3)" small label>
                    {{ formatDate(kiosk.lastPing) }}
                  </v-chip>
                </div>
                <div class="stat-item">
                  <span class="stat-label">Dernier ping TPA</span>
                  <v-chip :color="getDateColor(kiosk.terminalLastPing, 3)" small label>
                    {{ formatDate(kiosk.terminalLastPing) }}
                  </v-chip>
                </div>
                <div class="stat-item">
                  <span class="stat-label">N° de série TPA</span>
                  <span v-if="kiosk.tpaSerialNumber && kiosk.tpaSerialNumber !== 'N/A'" style="font-family: monospace; font-size: 13px; letter-spacing: .4px;
               padding: 2px 8px; border-radius: 4px; background: rgba(96,125,169,.32); border: 1px solid rgba(96,125,169,.5);">
                    {{ kiosk.tpaSerialNumber }}
                  </span>
                  <span v-else style="opacity: .4">—</span>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Energy Status -->
          <v-col cols="12" md="4">
            <v-card outlined class="fill-height">
              <v-card-title class="text-subtitle-1 pb-2">
                <v-icon left color="orange">mdi-flash</v-icon>
                Énergie
              </v-card-title>
              <v-card-text>
                <div class="stat-item">
                  <span class="stat-label">Tension batterie</span>
                  <v-chip :color="getVoltageColor(kiosk.batteryVoltage)" small label>
                    {{ kiosk.batteryVoltage }} V
                  </v-chip>
                </div>
                <div class="stat-item">
                  <span class="stat-label">Tension panneaux solaires</span>
                  <v-chip color="grey lighten-1" small label>
                    {{ kiosk.solarPanelVoltage }} V
                  </v-chip>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Sales Status -->
          <v-col cols="12" md="4">
            <v-card outlined class="fill-height">
              <v-card-title class="text-subtitle-1 pb-2">
                <v-icon left color="green">mdi-cart</v-icon>
                Ventes (4 derniers jours)
              </v-card-title>
              <v-card-text>
                <div class="stat-item">
                  <span class="stat-label">Dernière vente</span>
                  <v-chip :color="getDateColor(kiosk.lastSale, 3)" small label>
                    {{ formatDate(kiosk.lastSale) }}
                  </v-chip>
                </div>
                <div class="stat-item">
                  <span class="stat-label">Nombre de ventes</span>
                  <v-chip color="primary" small label>{{ kiosk.salesCount }}</v-chip>
                </div>
                <div class="stat-item">
                  <span class="stat-label">Validées / Annulées</span>
                  <div>
                    <v-chip :color="kiosk.validatedSales >= 70 ? 'success' : 'warning'" small label class="mr-1">
                      {{ kiosk.validatedSales }}%
                    </v-chip>
                    <v-chip :color="kiosk.cancelledSales > 30 ? 'error' : 'success'" small label>
                      {{ kiosk.cancelledSales }}%
                    </v-chip>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Stock & Errors -->
        <v-row class="mt-2">
          <v-col cols="12" md="6">
            <v-card outlined class="fill-height">
              <v-card-title class="text-subtitle-1 pb-2">
                <v-icon left color="teal">mdi-bottle-wine</v-icon>
                Stock
              </v-card-title>
              <v-card-text>
                <div class="d-flex align-center justify-space-around">
                  <div class="text-center">
                    <div class="text-h3 font-weight-bold teal--text">{{ kiosk.fullBottleCount || 0 }}</div>
                    <div class="text-caption grey--text">Bouteilles pleines</div>
                  </div>
                  <v-divider vertical class="mx-4" />
                  <div class="text-center">
                    <div class="text-h3 font-weight-bold"
                      :class="kiosk.errorLockerCount > 0 ? 'error--text' : 'success--text'">
                      {{ kiosk.errorLockerCount || 0 }}
                    </div>
                    <div class="text-caption grey--text">Portes en erreur</div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Tags -->
          <v-col cols="12" md="6">
            <v-card outlined class="fill-height">
              <v-card-title class="text-subtitle-1 pb-2">
                <v-icon left color="purple">mdi-tag-multiple</v-icon>
                Tags
              </v-card-title>
              <v-card-text>
                <div v-if="kiosk.tags && kiosk.tags.length > 0" class="d-flex flex-wrap">
                  <v-chip v-for="tag in kiosk.tags" :key="tag.id" color="purple" outlined small class="ma-1">
                    {{ tag.name }}
                  </v-chip>
                </div>
                <div v-else class="grey--text text-center">Aucun tag</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Racks Section -->
        <v-row class="mt-2" v-if="kiosk.racks && kiosk.racks.length > 0">
          <v-col cols="12">
            <v-card outlined>
              <v-card-title class="text-subtitle-1 pb-2">
                <v-icon left color="orange">mdi-server</v-icon>
                Communication des Racks
                <v-spacer />
                <v-chip :color="racksInError > 0 ? 'error' : 'success'" small>
                  {{ racksOk }} / {{ kiosk.racks.length }} OK
                </v-chip>
              </v-card-title>
              <v-card-text>
                <div class="racks-grid">
                  <v-tooltip bottom v-for="rack in kiosk.racks" :key="rack.name">
                    <template v-slot:activator="{ on, attrs }">
                      <v-card v-bind="attrs" v-on="on" :color="rack.comOk === 'OK' ? 'success' : 'error'" dark
                        class="rack-card text-center pa-2">
                        <div class="text-h6 font-weight-bold">{{ rack.name }}</div>
                        <div class="text-caption">{{ rack.voltage }} V</div>
                      </v-card>
                    </template>
                    <span>
                      Rack {{ rack.name }}<br>
                      État: {{ rack.comOk }}<br>
                      Tension: {{ rack.voltage }} V
                    </span>
                  </v-tooltip>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Tickets Section -->
        <v-row class="mt-2" v-if="kiosk.tickets && kiosk.tickets.length > 0">
          <v-col cols="12">
            <v-card outlined color="orange lighten-5">
              <v-card-title class="text-subtitle-1 pb-2">
                <v-icon left color="warning">mdi-ticket</v-icon>
                Tickets Zoho Desk
                <v-spacer />
                <v-chip color="warning" small dark>
                  {{ kiosk.tickets.length }} ticket(s) ouvert(s)
                </v-chip>
              </v-card-title>
              <v-card-text>
                <v-simple-table>
                  <thead>
                    <tr>
                      <th class="text-left">ID</th>
                      <th class="text-left" style="min-width: 300px">Sujet</th>
                      <th class="text-center">Statut</th>
                      <th class="text-left">Date création</th>
                      <th class="text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="ticket in kiosk.tickets" :key="ticket.id">
                      <td class="font-weight-medium">{{ ticket.id }}</td>
                      <td>{{ ticket.subject }}</td>
                      <td class="text-center">
                        <v-chip small :color="getTicketStatusColor(ticket.status)" dark>
                          {{ ticket.status }}
                        </v-chip>
                      </td>
                      <td>{{ formatDate(ticket.createdTime) }}</td>
                      <td class="text-center">
                        <v-btn small color="primary" :href="ticket.link" target="_blank" depressed>
                          <v-icon left small>mdi-open-in-new</v-icon>
                          Ouvrir
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Actions -->
      <v-divider />
      <v-card-actions class="pa-4">
        <v-btn color="primary" outlined @click="$emit('open-comment', kiosk)">
          <v-icon left>mdi-comment-text-outline</v-icon>
          Commentaires
        </v-btn>
        <v-btn color="secondary" outlined @click="copyStatusToClipboard" class="ml-2">
          <v-icon left>mdi-content-copy</v-icon>
          Copier le résumé
        </v-btn>
        <v-spacer />
        <v-btn text @click="$emit('input', false)">Fermer</v-btn>
      </v-card-actions>

      <!-- Snackbar for copy confirmation -->
      <v-snackbar v-model="snackbar" :timeout="2000" color="success">
        Résumé copié dans le presse-papier !
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbar = false">Fermer</v-btn>
        </template>
      </v-snackbar>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'KioskDetailDialog',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    kiosk: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      snackbar: false,
      alertDefinitions: [
        { key: 'salesAlert', label: 'Vente', color: 'purple', icon: 'mdi-cart-off' },
        { key: 'tpaAlert', label: 'TPA', color: 'green', icon: 'mdi-credit-card-off' },
        { key: 'pingAlert', label: 'Ping', color: 'blue', icon: 'mdi-wifi-off' },
        { key: 'cancelledSalesAlert', label: 'Ventes annulées', color: 'yellow darken-2', icon: 'mdi-cart-remove' },
        { key: 'rackAlert', label: 'Rack', color: 'orange', icon: 'mdi-server-off' },
        { key: 'voltageAlert', label: 'Tension', color: 'red', icon: 'mdi-flash-alert' },
        { key: 'motherboardFreezeAlert', label: 'Carte mère', color: 'cyan', icon: 'mdi-snowflake-alert' },
        { key: 'fuseAlert', label: 'Fusible', color: 'pink', icon: 'mdi-fuse-alert' },
        { key: 'batteryAlert', label: 'Batterie', color: 'deep-orange', icon: 'mdi-battery-alert' }
      ]
    };
  },
  computed: {
    activeAlerts() {
      if (!this.kiosk) return [];
      return this.alertDefinitions.filter(alert => this.kiosk[alert.key]);
    },
    racksOk() {
      if (!this.kiosk || !this.kiosk.racks) return 0;
      return this.kiosk.racks.filter(r => r.comOk === 'OK').length;
    },
    racksInError() {
      if (!this.kiosk || !this.kiosk.racks) return 0;
      return this.kiosk.racks.filter(r => r.comOk !== 'OK').length;
    }
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr || dateStr === 'N/A') return 'N/A';
      const date = new Date(dateStr);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    getDateColor(dateStr, daysThreshold) {
      if (!dateStr || dateStr === 'N/A') return 'grey';
      const date = new Date(dateStr);
      const now = new Date();
      const diffDays = (now - date) / (1000 * 60 * 60 * 24);
      if (diffDays > daysThreshold) return 'error';
      if (diffDays > 1) return 'warning';
      return 'success';
    },
    getVoltageColor(voltage) {
      if (!voltage || voltage === 'N/A') return 'grey';
      const v = parseFloat(voltage);
      if (v < 11.7) return 'error';
      if (v < 12) return 'warning';
      return 'success';
    },

    getTicketStatusColor(status) {
      if (!status) return 'grey';
      const s = status.toLowerCase();
      if (s.includes('open')) return 'warning';
      if (s.includes('hold')) return 'orange';
      if (s.includes('escalated')) return 'error';
      if (s.includes('closed') || s.includes('resolved')) return 'success';
      return 'grey';
    },

    generateStatusSummary() {
      if (!this.kiosk) return '';

      const k = this.kiosk;
      const lines = [];

      // Header
      lines.push(`📍 ÉTAT DE LA MACHINE : ${k.name} (${k.id})`);
      lines.push(`📅 Date du rapport : ${new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`);
      lines.push('');

      // Alerts
      if (this.activeAlerts.length > 0) {
        lines.push(`⚠️ ALERTES ACTIVES (${this.activeAlerts.length}) :`);
        this.activeAlerts.forEach(alert => {
          lines.push(`   • ${alert.label}`);
        });
        lines.push('');
      }

      // Connection
      lines.push('🔗 CONNEXION :');
      lines.push(`   • Dernier ping Nuage : ${this.formatDate(k.lastPing)}`);
      lines.push(`   • Dernier ping TPA : ${this.formatDate(k.terminalLastPing)}`);
      lines.push('');

      // Energy
      lines.push('⚡ ÉNERGIE :');
      lines.push(`   • Tension batterie : ${k.batteryVoltage} V`);
      lines.push(`   • Tension panneaux solaires : ${k.solarPanelVoltage} V`);
      lines.push('');

      // Sales
      lines.push('🛒 VENTES (4 derniers jours) :');
      lines.push(`   • Dernière vente : ${this.formatDate(k.lastSale)}`);
      lines.push(`   • Nombre de ventes : ${k.salesCount}`);
      lines.push(`   • Validées : ${k.validatedSales}% | Annulées : ${k.cancelledSales}%`);
      lines.push('');

      // Stock
      lines.push('📦 STOCK :');
      lines.push(`   • Bouteilles pleines : ${k.fullBottleCount || 0}`);
      lines.push(`   • Portes en erreur : ${k.errorLockerCount || 0}`);
      lines.push('');

      // Racks
      if (k.racks && k.racks.length > 0) {
        const racksKO = k.racks.filter(r => r.comOk !== 'OK');
        lines.push(`🖥️ RACKS (${this.racksOk}/${k.racks.length} OK) :`);
        if (racksKO.length > 0) {
          lines.push(`   • Racks en erreur : ${racksKO.map(r => `${r.name} (${r.voltage}V)`).join(', ')}`);
        } else {
          lines.push('   • Tous les racks sont OK');
        }
        lines.push('');
      }

      lines.push('─'.repeat(40));

      return lines.join('\n');
    },

    async copyStatusToClipboard() {
      const summary = this.generateStatusSummary();
      try {
        await navigator.clipboard.writeText(summary);
        this.snackbar = true;
      } catch (err) {
        console.error('Erreur lors de la copie :', err);
        // Fallback pour les navigateurs plus anciens
        const textArea = document.createElement('textarea');
        textArea.value = summary;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        this.snackbar = true;
      }
    }
  }
};
</script>

<style scoped>
.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #eee;
}

.stat-item:last-child {
  border-bottom: none;
}

.stat-label {
  font-size: 14px;
  color: #666;
}

.racks-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(60px, 1fr));
  gap: 8px;
}

.rack-card {
  min-width: 50px;
  border-radius: 8px;
}
</style>
